import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { isDemoSession } from '../../services/demoSession';
import { useDevices } from '../../context/DeviceContext';
import {
  SUBSCRIPTION_STATUS, STATUS_LABEL, LIMIT_LABEL, FEATURE_LABEL,
  formatLimit, PLAN_ID,
} from '../../domain/subscription';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import './Subscription.css';

const precio = (p) =>
  p.amount === 0
    ? 'Gratis'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: p.currency, maximumFractionDigits: 0 }).format(p.amount);

const fecha = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

/** Estado actual de la cuenta, arriba del todo: responde "¿qué tengo hoy?". */
function EstadoActual({ subscription, plan, devices, onCancel, onResume, processing }) {
  const s = subscription?.status;
  const cancelada = subscription?.cancelAtPeriodEnd;

  const tono =
    s === SUBSCRIPTION_STATUS.PAST_DUE ? 'error'
    : cancelada ? 'warning'
    : plan.id === PLAN_ID.FREE ? 'neutral'
    : 'success';

  return (
    <section className={`sb-current sb-current--${tono}`}>
      <div className="sb-current__main">
        <span className="sb-current__badge">
          {plan.id === PLAN_ID.FREE ? <LockRoundedIcon fontSize="small" /> : <WorkspacePremiumRoundedIcon fontSize="small" />}
          Plan {plan.name}
        </span>
        <h2 className="sb-current__title">
          {cancelada
            ? 'Tu suscripción termina pronto'
            : s === SUBSCRIPTION_STATUS.PAST_DUE
            ? 'Hay un problema con tu pago'
            : plan.id === PLAN_ID.FREE
            ? 'Estás en el plan gratuito'
            : 'Tu suscripción está activa'}
        </h2>
        <p className="sb-current__meta">
          {cancelada
            ? `Conservas el acceso hasta el ${fecha(subscription.currentPeriodEnd)}.`
            : plan.id === PLAN_ID.FREE
            ? `Usas ${devices.length} de ${formatLimit('maxDevices', plan.limits.maxDevices)}.`
            : `Se renueva el ${fecha(subscription?.currentPeriodEnd)}.`}
          {subscription?.paymentMethod &&
            ` · ${subscription.paymentMethod.brand.toUpperCase()} ····${subscription.paymentMethod.last4}`}
        </p>
      </div>

      {plan.id !== PLAN_ID.FREE && (
        <div className="sb-current__actions">
          <span className={`chip ${tono === 'success' ? 'success' : tono === 'error' ? 'error' : 'warning'}`}>
            {STATUS_LABEL[s] ?? 'Activa'}
          </span>
          {cancelada ? (
            <button className="sb-btn" onClick={onResume} disabled={processing}>Reactivar</button>
          ) : (
            <button className="sb-btn sb-btn--quiet" onClick={onCancel} disabled={processing}>Cancelar</button>
          )}
        </div>
      )}
    </section>
  );
}

export default function Subscription() {
  const { plans, plan: planActual, subscription, subscribe, cancel, resume, processing, loading } = useSubscription();
  const { devices } = useDevices();

  /* Los planes se muestran en toda cuenta, pero contratarlos solo funciona en la
     demostración: en una cuenta real no hay pasarela de pago ni backend de
     suscripciones. Se apaga el botón en vez de dejar que lleve a un error. */
  const altaDisponible = isDemoSession();

  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);
  const [confirmando, setConfirmando] = useState(null);

  /* Todas las claves de límite y funcionalidad que aparecen en cualquier plan.
     Se derivan del catálogo: añadir un beneficio nuevo no requiere tocar esta
     pantalla, solo el catálogo del dominio. */
  const clavesLimite = [...new Set(plans.flatMap((p) => Object.keys(p.limits || {})))];
  const clavesFeature = [...new Set(plans.flatMap((p) => Object.keys(p.features || {})))];

  const contratar = async (planId, simulateFailure = false) => {
    setError(null); setExito(null);
    try {
      await subscribe(planId, { simulateFailure });
      setExito(planId);
      setConfirmando(null);
    } catch (err) {
      setError(err.message || 'No se pudo completar la operación');
    }
  };

  if (loading) {
    return (
      <div className="sb-page">
        <div className="sb-current sb-skeleton" />
        <div className="sb-plans">
          {[0, 1].map((i) => <div key={i} className="sb-plan sb-skeleton" />)}
        </div>
      </div>
    );
  }

  /* Sin catálogo no hay nada honesto que mostrar. Ocurre en cuentas reales,
     donde el backend de suscripciones todavía no existe: antes se rellenaba
     con planes y precios inventados. */
  if (plans.length === 0) {
    return (
      <div className="sb-page">
        <div className="sb-unavailable">
          <span className="sb-unavailable__icon"><WorkspacePremiumRoundedIcon /></span>
          <h2>Los planes aún no están disponibles</h2>
          <p>
            Estamos preparando las suscripciones. Mientras tanto tu cuenta funciona
            con todas las funciones activas y sin coste.
          </p>
          <p className="sb-unavailable__note">
            Te avisaremos por correo en cuanto puedas elegir plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-page">
      <EstadoActual
        subscription={subscription}
        plan={planActual}
        devices={devices}
        processing={processing}
        onCancel={cancel}
        onResume={resume}
      />

      {exito && (
        <div className="sb-alert sb-alert--success" role="status">
          <CheckCircleRoundedIcon fontSize="small" />
          <span>Listo. Tu plan se actualizó correctamente.</span>
        </div>
      )}
      {error && (
        <div className="sb-alert sb-alert--error" role="alert">
          <ErrorRoundedIcon fontSize="small" />
          <span>{error}</span>
        </div>
      )}

      <div className="sb-plans">
        {plans.map((p, i) => {
          const esActual = p.id === planActual.id;
          return (
            <article
              key={p.id}
              className={`sb-plan ${p.highlighted ? 'is-featured' : ''} ${esActual ? 'is-current' : ''}`}
              style={{ '--i': i }}
            >
              <header className="sb-plan__head">
                {p.highlighted && <span className="sb-plan__ribbon">Recomendado</span>}
                <h3 className="sb-plan__name">{p.name}</h3>
                <p className="sb-plan__tagline">{p.tagline}</p>
                <p className="sb-plan__price">
                  <span className="sb-plan__amount">{precio(p.price)}</span>
                  {p.price.amount > 0 && <span className="sb-plan__interval">/mes</span>}
                </p>
              </header>

              <div className="sb-plan__section">
                <span className="sb-plan__section-title">Límites</span>
                <ul className="sb-list">
                  {clavesLimite.map((k) => (
                    <li key={k} className="sb-list__row">
                      <span className="sb-list__label">{LIMIT_LABEL[k] ?? k}</span>
                      <span className="sb-list__value">{formatLimit(k, p.limits[k])}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sb-plan__section">
                <span className="sb-plan__section-title">Incluye</span>
                <ul className="sb-list">
                  {clavesFeature.map((k) => {
                    const incluido = Boolean(p.features[k]);
                    return (
                      <li key={k} className={`sb-list__row ${incluido ? '' : 'is-off'}`}>
                        <span className={`sb-check ${incluido ? 'is-on' : ''}`}>
                          {incluido ? <CheckRoundedIcon fontSize="inherit" /> : <CloseRoundedIcon fontSize="inherit" />}
                        </span>
                        <span className="sb-list__label">{FEATURE_LABEL[k] ?? k}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <footer className="sb-plan__foot">
                {esActual ? (
                  <button className="sb-btn sb-btn--block" disabled>Tu plan actual</button>
                ) : confirmando === p.id ? (
                  <div className="sb-confirm">
                    <p>
                      {p.price.amount > 0
                        ? `Se te cobrarán ${precio(p.price)} cada mes.`
                        : 'Bajarás al plan gratuito al terminar el periodo actual.'}
                    </p>
                    <div className="sb-confirm__actions">
                      <button className="sb-btn sb-btn--sm" onClick={() => setConfirmando(null)} disabled={processing}>
                        Volver
                      </button>
                      <button
                        className="sb-btn sb-btn--sm sb-btn--primary"
                        onClick={() => contratar(p.id)}
                        disabled={processing}
                      >
                        {processing ? 'Procesando…' : 'Confirmar'}
                      </button>
                    </div>
                  </div>
                ) : !altaDisponible ? (
                  /* Sin pasarela de pago el alta no existe. Mas vale un boton
                     apagado que uno que lleva a un error. */
                  <button className="sb-btn sb-btn--block" disabled>Próximamente</button>
                ) : (
                  <button
                    className={`sb-btn sb-btn--block ${p.highlighted ? 'sb-btn--primary' : ''}`}
                    onClick={() => setConfirmando(p.id)}
                    disabled={processing}
                  >
                    {p.price.amount > planActual.price.amount ? `Cambiar a ${p.name}` : `Bajar a ${p.name}`}
                  </button>
                )}
              </footer>
            </article>
          );
        })}
      </div>

      <p className="sb-disclaimer">
        Los beneficios y precios mostrados son provisionales y no constituyen una oferta.
        El cobro todavía no está conectado a ningún proveedor de pagos: esta pantalla simula el flujo.
      </p>
    </div>
  );
}
